import { useState, type CSSProperties } from "react";
import { displayZone } from "../../../lib/format";
import { t, istCreatorKonto } from "../../../lib/sprache";
import { Seitenansicht, SeitenDruck } from "./Seitenansicht";
import { AUTHORITY_LABEL, SIGNING_AUTHORITIES, belegAkzent, systemVariable, type ContractRole, type CustomVariableDef, type Language, type SigningAuthority } from "@talentkontor/domain";
import { sanitizeHtml } from "../../../lib/sicheres-html";
import { useInvoiceLogo } from "../../../model/invoice-document";
import { day } from "./protokoll";
import "./vertraege.css";

/** Stand einer Unterschrift für die Anzeige im Papier (je Unterzeichner; eine Partei kann mehrere haben). */
export interface SignatureMark {
  role: ContractRole;
  slot?: number;
  capacity?: "party" | "legal_guardian" | null;
  signed: boolean;
  signingName?: string | null;
  signingPosition?: string | null;
  signingAuthority?: string | null;
  signedAt?: string | null;
  signatureData?: string | null;
  /** Name des vorgesehenen Unterzeichners (vor der Unterschrift) */
  name?: string | null;
  /** auf Papier unterschrieben und von der Agentur nachgetragen */
  paper?: boolean;
  /** Datum auf dem Papier ('YYYY-MM-DD'); signedAt ist dann die Erfassung (VERTRAEGE-02) */
  paperSignedOn?: string | null;
}

/** Texte der Unterschriftsfelder in der Sprache des Vertrags (Befund 8). */
export const PAPER_TEXT: Record<Language, { roles: Record<ContractRole, string>; open: string; signedOn: string; guardian: string; signature: string; paper: string; paperOn: string; recordedOn: string; date: string }> = {
  // Creator-Konto: die Rolle agency ist das Konto selbst (0197)
  de: { roles: { get agency() { return istCreatorKonto() ? "Creator" : "Agentur"; }, brand: "Marke", talent: "Creator" }, open: "Noch nicht unterschrieben", signedOn: "unterschrieben am", guardian: "gesetzliche Vertretung", signature: "Unterschrift", paper: "auf Papier unterschrieben", paperOn: "auf Papier unterschrieben am", recordedOn: "erfasst am", date: "Datum" },
  en: { roles: { get agency() { return istCreatorKonto() ? "Creator" : "Agency"; }, brand: "Brand", talent: "Creator" }, open: "Not signed yet", signedOn: "signed on", guardian: "legal guardian", signature: "Signature", paper: "signed on paper", paperOn: "signed on paper on", recordedOn: "recorded on", date: "Date" },
};

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function dateTime(iso: string, timeZone: string, lang: Language): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // mit Kurzname der Zeitzone: dieselbe Unterschrift zeigt überall dieselbe Uhrzeit (Befund 28)
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone, timeZoneName: "short" }).format(d)
    // Zeitzone nie allein in der nächsten Zeile (Layout-Prüfung L04)
    .replace(/ (\S+)$/, "\u00A0$1");
}

const PNG = /^data:image\/png;base64,[A-Za-z0-9+/]+=*$/;

function authorityText(a: string | null | undefined, lang: Language): string | null {
  return a && (SIGNING_AUTHORITIES as readonly string[]).includes(a) ? AUTHORITY_LABEL[a as SigningAuthority][lang] : null;
}

function signatureBox(role: ContractRole, mark: SignatureMark | undefined, timeZone: string, roleLabels: Record<ContractRole, string>, lang: Language): string {
  const t = PAPER_TEXT[lang];
  const label = esc(roleLabels[role]) + (mark?.capacity === "legal_guardian" ? ` · ${esc(t.guardian)}` : "");
  // Inhaber 29.10.2026: Unterschriften als Karten nebeneinander (Rolle, Datum, Unterschrift, Name, Funktion) wie im TrueTalent-Portal
  if (mark?.signed) {
    const image = mark.signatureData && PNG.test(mark.signatureData)
      ? `<img alt="${esc(t.signature)} ${esc(mark.signingName ?? "")}" src="${mark.signatureData}">`
      : `<span class="tk-sig-schrift">${esc(mark.signingName ?? "")}</span>`;
    const rest = [mark.signingPosition, authorityText(mark.signingAuthority, lang)].filter(Boolean).map((x) => esc(String(x))).join(", ");
    // Auf Papier: Datum auf dem Papier und Zeitpunkt der Erfassung, nie die Erfassung als Unterschriftsdatum (VERTRAEGE-02)
    const at = mark.paper
      ? `${esc(mark.paperSignedOn ? `${t.paperOn} ${day(mark.paperSignedOn, lang)}` : t.paper)}${mark.signedAt ? ` · ${esc(t.recordedOn)} ${esc(dateTime(mark.signedAt, timeZone, lang))}` : ""}`
      : mark.signedAt ? `${esc(t.date)}: <b>${esc(dateTime(mark.signedAt, timeZone, lang))}</b>` : "";
    const meta = `<strong>${esc(mark.signingName ?? "")}</strong>${rest ? `<span>${rest}</span>` : ""}`;
    return `<div class="tk-signature tk-sig-box" data-role="${role}"><div class="tk-sig-rolle">${label}</div><div class="tk-sig-datum">${at}</div><div class="tk-sig-image">${image}</div><div class="tk-sig-line"></div><div class="tk-sig-meta">${meta}</div></div>`;
  }
  const who = mark?.name ? `<strong>${esc(mark.name)}</strong>` : "";
  return `<div class="tk-signature tk-sig-box tk-sig-open" data-role="${role}"><div class="tk-sig-rolle">${label}</div><div class="tk-sig-datum">${esc(t.open)}</div><div class="tk-sig-image"></div><div class="tk-sig-line"></div><div class="tk-sig-meta">${who}</div></div>`;
}

/** Aufeinanderfolgende Unterschriftsfelder stehen in einer Reihe (Karten nebeneinander, schmal untereinander). */
const SIG_BOX = String.raw`<div class="tk-signature tk-sig-box[^"]*" data-role="(?:agency|brand|talent)">(?:<div class="tk-sig-[a-z]+">(?:(?!<\/div>)[\s\S])*<\/div>){5}<\/div>`;
const SIG_RUN = new RegExp(`(?:${SIG_BOX}\\s*)+`, "g");
function signatureRows(html: string): string {
  return html.replace(SIG_RUN, (run) => `<div class="tk-sig-reihe">${run.trim().replace(/(<\/div>)\s+(?=<div class="tk-signature)/g, "$1")}</div>`);
}

/** Markup für Feld mit Inhalt (Prüfung 0–2, Befund 17): der Inhalt steht danach in einem eigenen Block, wie im PDF */
const OPEN_DIV = "<div>";
const PAGE_BREAK_THEN_DIV = '<div class="tk-page-break"></div><div>';

/**
 * Vertragstext für die Anzeige: bereinigt (sicheres HTML), Unterschriftsfelder mit Stand (je Unterzeichner ein Feld),
 * offene Ausfüllfelder beschriftet. Reiner Text-Umbau; die gespeicherte Fassung bleibt unverändert.
 */
export function decorateContractHtml(html: string, opts: {
  marks?: readonly SignatureMark[];
  defs?: readonly CustomVariableDef[];
  timeZone?: string;
  roleLabels?: Record<ContractRole, string>;
  lang?: Language;
}): string {
  const clean = sanitizeHtml(html);
  const lang = opts.lang ?? "de";
  const tz = opts.timeZone ?? displayZone();
  const labels = opts.roleLabels ?? PAPER_TEXT[lang].roles;
  // Unterschriftsfeld wie im PDF (Prüfung 0–2, Befund 17): jedes Feld wird gezeichnet, auch eins mit Inhalt; der Inhalt steht
  // danach als eigener Absatz (so druckt ihn das PDF). Der Server lässt Felder mit Inhalt nicht mehr zu (content:tk-signature).
  const withSig = clean.replace(/<div class="tk-signature" data-role="(agency|brand|talent)">(<\/div>)?/g, (_, role: ContractRole, closed: string | undefined) => {
    const marks = (opts.marks ?? []).filter((m) => m.role === role).sort((a, b) => (a.slot ?? 1) - (b.slot ?? 1));
    const boxes = marks.length ? marks.map((m) => signatureBox(role, m, tz, labels, lang)).join("") : signatureBox(role, undefined, tz, labels, lang);
    return closed ? boxes : boxes + OPEN_DIV;
  }).replace(/<div class="tk-page-break">(?!<\/div>)/g, PAGE_BREAK_THEN_DIV);
  const withRows = signatureRows(withSig);
  const withFill = withRows.replace(/<span class="tk-fill" data-var="([a-z][a-z0-9_]{0,63})"><\/span>/g, (_, key: string) => {
    const def = opts.defs?.find((d) => d.key === key);
    const who = def?.fillable_by ? (lang === "en" ? ` · to be filled in by ${labels[def.fillable_by]}` : ` · trägt ${labels[def.fillable_by]} ein`) : "";
    const label = lang === "en" ? def?.label.en || def?.label.de : def?.label.de;
    return `<span class="tk-fill tk-fill-open" data-var="${key}">${esc(label ?? key)}${esc(who)}</span>`;
  });
  // Offene Platzhalter sichtbar machen (nur im Entwurf möglich; versendete Verträge enthalten keine mehr)
  // K-564 a: leere eigene Angabe als benannte Lücke („[Korrekturschleifen je Inhalt]“), im Editor anklickbar wie tk-fill
  return withFill.replace(/\{\{[^{}<>]{1,120}\}\}/g, (m) => {
    const key = /^\{\{\s*var\.([a-z][a-z0-9_]{0,63})\s*(?:\|[^}]*)?\}\}$/.exec(m)?.[1];
    const def = key ? opts.defs?.find((d) => d.key === key) : undefined;
    if (!def) {
      // CO-018: fehlender Systemwert (z. B. Anschrift des Creators) ebenso als benannte Lücke statt {{talent.address_block}}
      const sys = key ? undefined : systemVariable(/^\{\{\s*([a-z_]+\.[a-z0-9_]+)\s*(?:\|[^}]*)?\}\}$/.exec(m)?.[1] ?? "");
      if (!sys) return `<span class="tk-unresolved">${m}</span>`;
      const name = (lang === "en" ? sys.label.en : sys.label.de).replace(/\s*\([^)]*\)$/, "");
      return `<span class="tk-unresolved tk-gap">[${esc(name)}]</span>`;
    }
    const label = lang === "en" ? def.label.en || def.label.de : def.label.de;
    return `<span class="tk-unresolved tk-gap" data-var="${key}">[${esc(label)}]</span>`;
  });
}

/**
 * Überschriften des Vertragstexts eine Ebene tiefer (SICHT-OEFFENTLICH-BETRIEB-21): Das Papier steht fast immer unter dem
 * h1 der Seite (Signierseite, Vertrag, Entwurf); das h1 des Vertrags (meist derselbe Titel) war dann eine zweite h1. Aus
 * h1–h4 wird h2–h5 mit der Klasse tk-ph1–tk-ph4, die das bisherige Aussehen hält (vertraege.css). Nur für die Anzeige; die
 * gespeicherte Fassung, PDF und Druckansicht (nested=false) bleiben unverändert.
 */
export function nestHeadings(html: string): string {
  return html.replace(/<(\/?)h([1-4])((?:\s[^>]*)?)>/gi, (_m, close: string, level: string, attrs: string) => {
    const tag = `h${Number(level) + 1}`;
    if (close) return `</${tag}>`;
    const cls = `tk-ph${level}`;
    const withClass = /\sclass="/i.test(attrs) ? attrs.replace(/(\sclass=")/i, `$1${cls} `) : `${attrs} class=${JSON.stringify(cls)}`;
    return `<${tag}${withClass}>`;
  });
}

export function VertragPapier({ html, annexHtml, eyebrow, marks, defs, timeZone, roleLabels, lang, nested = true, logoPath, accent, start = "flow", leiste, vermerk, seiten }: {
  html: string | null;
  annexHtml?: string | null;
  eyebrow?: string;
  marks?: readonly SignatureMark[];
  defs?: readonly CustomVariableDef[];
  timeZone?: string;
  roleLabels?: Record<ContractRole, string>;
  /** Sprache des Vertrags: bestimmt die Texte der Unterschriftsfelder */
  lang?: Language;
  /** Papier unter dem h1 der Seite (Standard): Überschriften des Vertrags eine Ebene tiefer; false in der Druckansicht */
  nested?: boolean;
  /** Logo der Agentur (Bucket branding) als Dokumentkopf, wie auf Rechnung und PDF; der Seitenrahmen trägt es nie */
  logoPath?: string | null;
  /** Agenturfarbe (org_settings.brand_color): Kopfzeile wie im PDF (ausfertigung.ts), sonst die Farbe des Portals */
  accent?: string | null;
  /** Darstellung beim Öffnen; die Vorschau (K-511) startet in den Seiten wie im PDF */
  start?: "flow" | "pages";
  /** Seitenleiste mit anklickbaren Seiten in der Seitenansicht (K-519) */
  leiste?: boolean;
  /** Vermerk wie im PDF auf jeder Seite, etwa „Entwurf – nicht unterschrieben“ (K-513) */
  vermerk?: string | null;
  /** Druckansicht (nested=false) in Seiten wie im PDF statt Fließtext (K-513) */
  seiten?: boolean;
}) {
  const logo = useInvoiceLogo(logoPath ?? null);
  // Seitenansicht wie im PDF (PARITAET-R3-8): am Bildschirm umschaltbar; die Druckansicht (nested=false) druckt Fließtext
  // oder, auf Wunsch, dieselben Seiten wie das PDF (K-513)
  const [mode, setMode] = useState<"flow" | "pages">(start);
  const shift = nested ? nestHeadings : (s: string) => s;
  const body = shift(decorateContractHtml(html ?? "", { marks, defs, timeZone, roleLabels, lang }));
  const annex = annexHtml ? shift(decorateContractHtml(annexHtml, { marks, defs, timeZone, roleLabels, lang })) : null;
  const farbe = belegAkzent(accent); // gleiche Helligkeitsgrenze wie im PDF; zu hell bleibt neutral
  const paper = (
    <article className={farbe ? "paper tk-akzent" : "paper"} lang={lang ?? "de"} style={farbe ? ({ "--tk-beleg-akzent": farbe } as CSSProperties) : undefined}>
      {logo?.url && <div className="tk-paper-logo tk-contract-logo"><img src={logo.url} alt="" /></div>}
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      {/* Inhalt ist durch sanitizeHtml bereinigt; Unterschriftsbilder nur als geprüfte PNG-data-URL */}
      <div dangerouslySetInnerHTML={{ __html: body }} />
      {annex && <div className="tk-annex" dangerouslySetInnerHTML={{ __html: annex }} />}
    </article>
  );
  if (!nested && seiten && html) {
    return <SeitenDruck html={html} annexHtml={annexHtml} marks={marks} lang={lang ?? "de"} timeZone={timeZone ?? displayZone()} roleLabels={roleLabels} title={eyebrow ?? t("Vertrag", "Contract")} vermerk={vermerk} accent={farbe} />;
  }
  if (!nested || !html) return paper;
  return (
    <div className="stack tk-paper-stack">
      <div className="tk-paper-mode tk-noprint">
        <div className="toolbar" role="group" aria-label={t("Darstellung des Vertrags", "Contract display")}>
          <button type="button" aria-pressed={mode === "flow"} onClick={() => setMode("flow")}>{t("Fließtext", "Continuous text")}</button>
          <button type="button" aria-pressed={mode === "pages"} onClick={() => setMode("pages")}>{t("Seiten wie im PDF", "Pages as in the PDF")}</button>
        </div>
      </div>
      {mode === "pages"
        ? <Seitenansicht html={html} annexHtml={annexHtml} marks={marks} lang={lang ?? "de"} timeZone={timeZone ?? displayZone()} roleLabels={roleLabels} title={eyebrow ?? t("Vertrag", "Contract")} vermerk={vermerk} leiste={leiste} accent={farbe} />
        : paper}
    </div>
  );
}

/** Unterschriftszeilen eines Vertrags als Marken fürs Papier. */
export function marksOf(signers: readonly { role: ContractRole; slot: number; capacity: "party" | "legal_guardian"; name: string; signed_at: string | null; invalidated_at?: string | null;
  signing_name: string | null; signing_position: string | null; signing_authority?: string | null; signature_data: string | null; paper_signed_on?: string | null }[]): SignatureMark[] {
  return signers.map((s) => {
    const signed = !!s.signed_at && !s.invalidated_at;
    return {
      role: s.role, slot: s.slot, capacity: s.capacity, signed, name: s.name,
      signingName: signed ? s.signing_name : null, signingPosition: signed ? s.signing_position : null,
      signingAuthority: signed ? s.signing_authority ?? null : null, signedAt: signed ? s.signed_at : null, signatureData: signed ? s.signature_data : null,
      paper: signed && !!s.paper_signed_on, paperSignedOn: signed ? s.paper_signed_on ?? null : null,
    };
  });
}
